import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Shield, Zap, Globe, ArrowRight, Lock, Scan, 
  Activity, BarChart3, Pause, Play, XCircle, Bell,
  Chrome, Smartphone, Monitor, Apple, AlertTriangle, Info, Loader2, Check, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { getBotConfigSync, refreshBotConfig, classifyDomain, extractDomain } from '@/lib/botConfig';

export default function HomePage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const botConfigRef = useRef(getBotConfigSync());
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  // Refresh config in background on mount
  useEffect(() => {
    refreshBotConfig().then((cfg) => { botConfigRef.current = cfg; });
  }, []);

  const domainCheck = useMemo(() => {
    const domain = extractDomain(url);
    if (!domain || url.trim().length < 4) return { tier: null, message: '' };
    return classifyDomain(domain, botConfigRef.current);
  }, [url]);

  const handleScan = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast.error('Please enter a URL to scan');
      return;
    }

    setLoading(true);
    try {
      const result = await api.createScan(url.trim());
      
      if (result.status === 'done' && result.public_report_id) {
        navigate(`/r/${result.public_report_id}`);
      } else {
        navigate(`/scan/${result.scan_id}`);
      }
    } catch (error) {
      console.error('Scan error:', error);
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again in a few minutes.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to start scan. Please check the URL and try again.');
      }
      setLoading(false);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail.trim(), source: 'homepage_monitor' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to join');
      setWaitlistDone(true);
      toast.success(data.message || "You're on the list!");
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AgentCheck
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#scan" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                Site Scan
              </a>
              <Link to="/monitor" className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                <Chrome className="w-4 h-4" />
                Monitor (Browser)
              </Link>
              <Link to="/pricing" className="text-sm text-[#007AFF] hover:text-[#0062CC] font-medium transition-colors">
                Pro
              </Link>
              <Link to="/leaderboard/ai-bots" className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                <BarChart3 className="w-4 h-4" />
                Leaderboard
              </Link>
              <Link to="/verify" className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                Verify
              </Link>
              <Link to="/agents" className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                <Activity className="w-4 h-4" />
                Agent Control Plane
              </Link>
              <Link to="/docs/api" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                API Docs
              </Link>
              <a href="#how-it-works" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                How it works
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-blue-50 to-transparent rounded-full opacity-60" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8 animate-fade-in-up">
            <Zap className="w-4 h-4 text-[#007AFF]" />
            <span className="text-sm font-medium text-[#007AFF]">Privacy audit in seconds</span>
          </div>

          {/* Headline - Updated for two-mode framing */}
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight mb-6 animate-fade-in-up stagger-1"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Who's tracking you —
            <br />
            <span className="text-[#007AFF]">and what's touching your device?</span>
          </h1>

          {/* Subheadline - Two-mode description */}
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-12 animate-fade-in-up stagger-2">
            <strong>Scan any website</strong> to instantly see the companies and trackers running behind the scenes.
            Or enable <strong>AgentCheck Monitor</strong> to see what's contacting your device in the background — continuously.
          </p>

          {/* Scan Form */}
          <form 
            onSubmit={handleScan}
            className="max-w-2xl mx-auto animate-fade-in-up stagger-3"
            data-testid="scan-form"
            id="scan"
          >
            <div className="relative flex items-center gap-3 p-2 bg-white rounded-full border-2 border-slate-200 shadow-lg shadow-slate-200/50 input-focus-ring transition-all">
              <div className="pl-4">
                <Globe className="w-5 h-5 text-slate-400" />
              </div>
              <Input
                type="text"
                placeholder="Enter a website URL (e.g., nytimes.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1 border-0 bg-transparent text-lg placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="url-input"
                disabled={loading}
              />
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="h-12 px-8 rounded-full bg-[#007AFF] hover:bg-[#0062CC] text-white font-semibold shadow-lg shadow-blue-500/20 btn-hover-scale"
                data-testid="scan-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Scanning...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Run Site Scan
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </div>

            {/* Pre-scan warning for bot-protected sites (tiered) */}
            {domainCheck.tier && url.trim().length > 3 && (
              <div className={`mt-3 flex items-start gap-2.5 px-5 py-3 rounded-xl border text-left animate-fade-in-up ${
                domainCheck.tier === 'A' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
              }`} data-testid="protected-domain-warning" data-tier={domainCheck.tier}>
                {domainCheck.tier === 'A'
                  ? <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  : <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`text-sm font-medium ${domainCheck.tier === 'A' ? 'text-amber-800' : 'text-blue-800'}`}>{domainCheck.message}</p>
                  <p className={`text-xs mt-0.5 ${domainCheck.tier === 'A' ? 'text-amber-700' : 'text-blue-600'}`}>For a complete view, use <button className="underline font-medium" onClick={(e) => { e.preventDefault(); navigate('/monitor'); }}>Monitor</button> while browsing.</p>
                </div>
              </div>
            )}
          </form>

          {/* Secondary CTA - Monitor */}
          <div className="mt-6 animate-fade-in-up stagger-4">
            <Button
              variant="outline"
              size="lg"
              className="h-12 px-8 rounded-full border-2 border-slate-200 hover:border-[#007AFF] hover:text-[#007AFF] font-semibold transition-colors"
              onClick={() => document.getElementById('monitor')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="monitor-cta"
            >
              <Activity className="w-5 h-5 mr-2" />
              Monitor My Device
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              Chrome extension (browser) • Android device monitor next • iOS/macOS later
            </p>
          </div>

          {/* Trust indicators - Updated for accuracy */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-10 text-sm text-slate-500 animate-fade-in-up stagger-5">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Anonymous site scans</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Domain-level results only</span>
            </div>
            <div className="flex items-center gap-2">
              <Scan className="w-4 h-4" />
              <span>No page content collected</span>
            </div>
          </div>
        </div>
      </section>

      {/* Two Modes Section */}
      <section className="py-20 bg-slate-50" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Choose your check
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Start with a quick site scan, or go deeper with continuous device monitoring.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Site Scan Card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-8 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                <Scan className="w-7 h-7 text-[#007AFF]" />
              </div>
              <h3 
                className="text-2xl font-semibold text-slate-900 mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Site Scan
              </h3>
              <p className="text-slate-500 text-sm uppercase tracking-wider mb-4">Instant • No install</p>
              <p className="text-slate-600 mb-6">
                See which companies and trackers are running on any website.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Companies detected (mapped from domains)',
                  'Tracker categories (Ads, Analytics, Social, Replay...)',
                  'Agent Pressure Score (APS)',
                  'Shareable report link'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => document.getElementById('scan')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-[#007AFF] hover:bg-[#0062CC] rounded-full h-12"
              >
                Scan a site
              </Button>
            </div>

            {/* Device Monitor Card */}
            <div className="bg-white rounded-2xl border-2 border-[#007AFF]/20 shadow-soft p-8 hover:shadow-md transition-shadow relative overflow-hidden" id="monitor">
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 rounded-full bg-blue-50 text-[#007AFF] text-xs font-semibold">
                  Coming Soon
                </span>
              </div>
              <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                <Activity className="w-7 h-7 text-[#007AFF]" />
              </div>
              <h3 
                className="text-2xl font-semibold text-slate-900 mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Monitor My Device
              </h3>
              <p className="text-slate-500 text-sm uppercase tracking-wider mb-4">Continuous • Requires install</p>
              <p className="text-slate-600 mb-6">
                See what's contacting you — across browsing and background activity.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  '24h / 48h / 30d / 90d / YTD graphs',
                  'Companies contacting you (ranked)',
                  'Shut / Pause / Continue controls',
                  'Alerts for "new trackers" (premium)'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="w-full border-2 border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF] hover:text-white rounded-full h-12"
                data-testid="join-waitlist-btn"
                onClick={() => setWaitlistOpen(true)}
              >
                Join Waitlist
              </Button>
              {waitlistOpen && (
                <div className="mt-3 animate-in slide-in-from-top-2 duration-200">
                  {waitlistDone ? (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="waitlist-success">
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <p className="text-sm text-emerald-700">You're on the list! We'll notify you at launch.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleWaitlist} className="flex gap-2" data-testid="waitlist-form">
                      <Input
                        type="email"
                        placeholder="you@email.com"
                        value={waitlistEmail}
                        onChange={(e) => setWaitlistEmail(e.target.value)}
                        required
                        className="flex-1 rounded-full h-10 text-sm"
                        data-testid="waitlist-email-input"
                        autoFocus
                      />
                      <Button
                        type="submit"
                        disabled={waitlistLoading}
                        className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full h-10 px-4"
                        data-testid="waitlist-submit-btn"
                      >
                        {waitlistLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      </Button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Platform roadmap */}
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-500 mb-4">Platform roadmap</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100">
                <Chrome className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-700 font-medium">Chrome — First</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 border border-slate-200">
                <Smartphone className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">Android — Next</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 border border-slate-200">
                <Monitor className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">macOS / Windows — Later</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 border border-slate-200">
                <Apple className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">iOS — Later</span>
              </div>
            </div>
          </div>

          {/* Trust note */}
          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
            Device monitoring requires installation and explicit permissions. AgentCheck never reads page content, passwords, or form inputs.
          </p>
        </div>
      </section>

      {/* About APS Section */}
      <section className="py-20" id="about">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              What is Agent Pressure Score (APS)?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              APS estimates how much third-party automation ("agents") a website or your browsing environment contains — 
              based on the number of distinct third parties, request volume, and category weights. 
              <strong className="text-slate-800"> It's explainable, not a black box.</strong>
            </p>
          </div>

          <div className="grid sm:grid-cols-4 gap-4 mb-12">
            {[
              { range: '0-25', label: 'Low', color: 'emerald', desc: 'Minimal tracking' },
              { range: '26-50', label: 'Moderate', color: 'amber', desc: 'Standard tracking' },
              { range: '51-75', label: 'High', color: 'orange', desc: 'Heavy tracking' },
              { range: '76-100', label: 'Very High', color: 'red', desc: 'Extreme tracking' }
            ].map((tier) => (
              <div key={tier.range} className={`p-4 rounded-xl bg-${tier.color}-50 border border-${tier.color}-100 text-center`}>
                <div className={`text-2xl font-bold text-${tier.color}-600 mb-1`} style={{ fontFamily: 'JetBrains Mono' }}>
                  {tier.range}
                </div>
                <div className={`text-sm font-semibold text-${tier.color}-700`}>{tier.label}</div>
                <div className="text-xs text-slate-500 mt-1">{tier.desc}</div>
              </div>
            ))}
          </div>

          {/* What are agents */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 
              className="text-xl font-semibold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              What are "Agents"?
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              In AgentCheck, "agents" are automated third-party scripts and trackers that run when you visit a website 
              or use your device. These include advertising pixels, analytics tools, social media widgets, 
              chat widgets, session replay tools, and more. Many websites load dozens of these agents — often without your knowledge.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {['Ads', 'Analytics', 'Social', 'Session Replay', 'Payments', 'Chat', 'CDP', 'Fraud Prevention'].map((cat) => (
                <div 
                  key={cat}
                  className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 text-center"
                >
                  {cat}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Start with a 10-second check
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            Scan any website instantly, or join the waitlist for continuous device monitoring.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => document.getElementById('scan')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="h-14 px-10 rounded-full bg-[#007AFF] hover:bg-[#0062CC] text-lg font-semibold shadow-lg shadow-blue-500/20"
            >
              <Scan className="w-5 h-5 mr-2" />
              Run Site Scan
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-14 px-10 rounded-full border-2 border-slate-200 hover:border-[#007AFF] text-lg font-semibold"
            >
              <Activity className="w-5 h-5 mr-2" />
              Join Monitor Waitlist
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-[#007AFF] flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" strokeWidth={2} />
              </div>
              <span className="text-sm font-semibold text-slate-700">AgentCheck</span>
            </div>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} AgentCheck. Privacy-first website & device scanning.
              {' '}<Link to="/privacy" className="text-[#007AFF] hover:underline">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
